import { type Organization } from "@entities";
import { organizationRepo } from "@repositories";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { remult } from "remult";
import { useUser } from "./UserProvider";
import { Roles } from "@shared/Roles";
import { useNavigate } from "react-router-dom";

interface OrganizationContextType {
  currOrganization: Organization | undefined;
  updateCurrOrganization<T extends keyof Organization>(
    whatToUpdate: T | Partial<Organization>,
    value?: Organization[T],
  ): Promise<void>;
  getCurrOrganization(): Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const OrganizationProvider = ({ children }: { children: ReactNode; }) => {
  const [currOrganization, setCurrOrganization] = useState<Organization>();
  const navigate = useNavigate();
  const { currentUser } = useUser();

  useEffect(() => {
    if (currentUser) {
      getCurrOrganization();
    }
  }, [currentUser?.id]);

  async function getCurrOrganization() {
    let organization = await organizationRepo.findOne({
      where: { id: currentUser?.organization?.id },
      include: {
        status: true,
        forms: { include: { categories: true, team: true } },
        categories: true,
        populations: true,
      },
    });
    setCurrOrganization(organization);
    if (remult.isAllowed(Roles.AdminsAndManager)) {
      if (["/", "/login"].includes(location.pathname))
        navigate("/volunteers", { replace: true });
    }
  }

  /**
   * Updates a specific field of the current user in the organization manager repository.
   *
   * @param whatToUpdate - The name of the field to be updated as string OR an object of `Organization` type.
   * @param value - The new value to be assigned to the specified field. The type is the type of the filed you entered. NOT NEEDED IF `whatToUpdate` is an object.
   * @returns void, the currOrganization will be changed.
   *
   * @throws Will throw an error if the update operation fails or if `currOrganization` is not defined.
   *
   * @example
   * // Update the organization's email
   * await updatedCurrentUser('email', 'yotamHamelech@example.com');
   *
   * // Update the organization's address
   * await updatedCurrentUser('address', 'HaPalmach 41st Jerusalem');
   */
  async function updateCurrOrganization<T extends keyof Organization>(
    whatToUpdate: T,
    value: Organization[T],
  ): Promise<void>;
  async function updateCurrOrganization(
    organization: Partial<Organization>,
  ): Promise<void>;
  async function updateCurrOrganization<T extends keyof Organization>(
    a: T | Partial<Organization>,
    b?: Organization[T],
  ): Promise<void> {
    if (!currOrganization?.id) throw new Error("No curr organization");
    if (typeof a === "object") {
      const updatedOrganization = await organizationRepo.update(
        currOrganization.id,
        {
          ...currOrganization,
          ...a,
        },
      );
      setCurrOrganization(updatedOrganization);
    } else {
      const updatedOrganization = await organizationRepo.update(
        currOrganization.id,
        { ...currOrganization, [a]: b },
      );
      setCurrOrganization(updatedOrganization);
      if (["id"].includes(a)) {
        (remult.user as unknown) = {
          ...remult.user,
          organizationId: updatedOrganization.id,
        };
      }
    }
  }

  return (
    <OrganizationContext.Provider
      value={{ currOrganization, updateCurrOrganization, getCurrOrganization }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
